import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Power, PowerOff } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import BotonAyuda from '../components/BotonAyuda';

const TIPO_INFO = {
  activo: { label: 'Activo', badge: 'bg-blue-50 text-blue-700' },
  pasivo: { label: 'Pasivo', badge: 'bg-orange-50 text-orange-700' },
  patrimonio: { label: 'Patrimonio', badge: 'bg-violet-50 text-violet-700' },
  ingreso: { label: 'Ingreso', badge: 'bg-green-50 text-green-700' },
  egreso: { label: 'Egreso', badge: 'bg-red-50 text-red-700' }
};

const FORM_VACIO = { codigo: '', nombre: '', tipo: 'activo', padre_id: '' };

function nivelDe(codigo) {
  return (String(codigo || '').match(/\./g) || []).length;
}

export default function PlanCuentas() {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  const [cuentas, setCuentas] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [error, setError] = useState('');

  async function cargar() {
    const { data } = await api.get('/plan-cuenta');
    setCuentas(data);
  }

  useEffect(() => {
    if (empresaActiva) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  function empezarEdicion(cuenta) {
    setEditandoId(cuenta.id);
    setForm({ codigo: cuenta.codigo, nombre: cuenta.nombre, tipo: cuenta.tipo, padre_id: cuenta.padre_id || '' });
    setError('');
    setMostrarForm(true);
  }

  function cancelar() {
    setMostrarForm(false);
    setEditandoId(null);
    setForm(FORM_VACIO);
    setError('');
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    const datos = {
      codigo: form.codigo.trim(),
      nombre: form.nombre.trim(),
      tipo: form.tipo,
      padre_id: form.padre_id || null
    };
    try {
      if (editandoId) {
        await api.put(`/plan-cuenta/${editandoId}`, datos);
      } else {
        await api.post('/plan-cuenta', datos);
      }
      cancelar();
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar la cuenta');
    }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar esta cuenta contable?')) return;
    try {
      await api.delete(`/plan-cuenta/${id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar la cuenta');
    }
  }

  async function toggleActivo(cuenta) {
    try {
      await api.patch(`/plan-cuenta/${cuenta.id}/activo`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo cambiar el estado');
    }
  }

  // Padres posibles: mismo tipo que el seleccionado, sin ella misma (el
  // servidor valida además que no se cree un ciclo).
  const padresPosibles = cuentas.filter((c) => c.tipo === form.tipo && c.id !== editandoId);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-slate-800">
          Plan de Cuentas
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>
        <button
          onClick={() => setMostrarForm(true)}
          title="Nueva cuenta"
          className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md self-start sm:self-auto"
        >
          <Plus size={18} />
        </button>
      </div>

      {mostrarForm && (
        <Modal title={editandoId ? 'Editando cuenta' : 'Nueva cuenta'} onClose={cancelar}>
          <form onSubmit={guardar} className="flex flex-wrap items-end gap-2">
            <div className="w-32">
              <label className="block text-xs text-slate-500 mb-1">Código</label>
              <input
                required
                placeholder="Ej. 1.1.01"
                inputMode="decimal"
                value={form.codigo}
                onChange={(e) => setForm({ ...form, codigo: e.target.value.replace(/[^0-9.]/g, '') })}
                className="border rounded-md px-3 py-2 w-full font-mono tabular-nums"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-slate-500 mb-1">Nombre</label>
              <input
                required
                placeholder="Ej. Banco Mercantil USD"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="border rounded-md px-3 py-2 w-full"
              />
            </div>
            <div className="min-w-[150px]">
              <label className="block text-xs text-slate-500 mb-1">Tipo</label>
              <select
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value, padre_id: '' })}
                className="border rounded-md px-3 py-2 w-full"
              >
                {Object.entries(TIPO_INFO).map(([valor, info]) => (
                  <option key={valor} value={valor}>{info.label}</option>
                ))}
              </select>
            </div>
            <div className="min-w-[220px]">
              <label className="block text-xs text-slate-500 mb-1">Cuenta padre (opcional)</label>
              <select
                value={form.padre_id}
                onChange={(e) => setForm({ ...form, padre_id: e.target.value })}
                className="border rounded-md px-3 py-2 w-full"
              >
                <option value="">Sin padre (cuenta mayor)</option>
                {padresPosibles.map((c) => (
                  <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>
                ))}
              </select>
            </div>
            <button type="submit" className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700">
              {editandoId ? 'Guardar cambios' : 'Agregar'}
            </button>
            {error && <p className="text-red-600 text-sm w-full">{error}</p>}
          </form>
        </Modal>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3 w-28">Código</th>
                <th className="p-3">Nombre</th>
                <th className="p-3 w-32">Tipo</th>
                <th className="p-3 w-24">Naturaleza</th>
                <th className="p-3 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {cuentas.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-slate-400">No hay cuentas contables creadas todavía.</td>
                </tr>
              )}
              {cuentas.map((c) => (
                <tr key={c.id} className={`border-t hover:bg-slate-50 transition-colors ${c.activo ? '' : 'opacity-50'}`}>
                  <td className="p-3 font-mono text-slate-500 tabular-nums">{c.codigo}</td>
                  <td className="p-3 text-slate-700" style={{ paddingLeft: `${12 + nivelDe(c.codigo) * 20}px` }}>
                    {c.nombre}
                    {c.total_hijos > 0 && <span className="ml-2 text-xs text-slate-400">({c.total_hijos})</span>}
                  </td>
                  <td className="p-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TIPO_INFO[c.tipo].badge}`}>
                      {TIPO_INFO[c.tipo].label}
                    </span>
                  </td>
                  <td className="p-3 text-slate-500 capitalize">{c.naturaleza}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-3 justify-end">
                      <button onClick={() => empezarEdicion(c)} title="Editar" className="text-slate-400 hover:text-primary-600 transition-colors">
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => toggleActivo(c)}
                        title={c.activo ? 'Inhabilitar' : 'Habilitar'}
                        className={`transition-colors ${c.activo ? 'text-slate-400 hover:text-red-600' : 'text-slate-400 hover:text-green-600'}`}
                      >
                        {c.activo ? <Power size={16} /> : <PowerOff size={16} />}
                      </button>
                      <button onClick={() => eliminar(c.id)} title="Eliminar" className="text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-slate-400">
        Base del modelo de datos de Partida Doble (#114). Todavía sin mapeo categoría→cuenta ni generación automática de asientos.
      </p>
    </div>
  );
}
